const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Format an ISO-8601 string as a coarse relative time ("2h ago", "yesterday").
 * Returns "—" for null/undefined inputs so callers don't have to guard.
 *
 * Coarse-grained on purpose: the gallery row needs to scan well, not be
 * pixel-precise. A real "Last run" timestamp lives in the run detail view.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = now.getTime() - then;
  if (diff < 0) return 'just now';
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 2 * DAY) return 'yesterday';
  if (diff < WEEK) return `${Math.floor(diff / DAY)}d ago`;
  if (diff < MONTH) return `${Math.floor(diff / WEEK)}w ago`;
  if (diff < YEAR) return `${Math.floor(diff / MONTH)}mo ago`;
  return `${Math.floor(diff / YEAR)}y ago`;
}

/**
 * Format a duration in milliseconds as a compact human label.
 * E.g. 7400 → "7s", 90000 → "1m 30s", 4_200_000 → "1h 10m".
 * Returns "—" for null / undefined / non-finite / negative inputs so callers
 * never have to defensively guard their stat values.
 *
 * Uses `Math.floor` (not `Math.round`) for the seconds bucket so a value
 * like 59,500ms stays "59s" rather than rounding up to "1m" — keeps the
 * label honest with the underlying number.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.floor(ms)}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60)
    return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes === 0
    ? `${hours}h`
    : `${hours}h ${remMinutes}m`;
}
